import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

// Types
export interface App {
  id: string;
  app_name: string;
  version: string;
  signup_bonus_enabled: boolean;
  signup_bonus_amount: string;
  created_at: string;
  updated_at: string;
}

export interface CreateAppData {
  app_name: string;
  version: string;
  signup_bonus_enabled?: boolean;
  signup_bonus_amount?: string;
}

export interface UpdateAppData {
  id: string;
  app_name?: string;
  version?: string;
  signup_bonus_enabled?: boolean;
  signup_bonus_amount?: string;
}

export interface AppsState {
  apps: App[];
  loading: boolean;
  error: string | null;
  success: string | null;
  totalCount: number;
}

const initialState: AppsState = {
  apps: [],
  loading: false,
  error: null,
  success: null,
  totalCount: 0,
};

// Async thunks
export const fetchApps = createAsyncThunk(
  'apps/fetchApps',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/apps/');
      console.log('Apps API Response:', response.data);
      
      if (Array.isArray(response.data)) {
        return {
          apps: response.data,
          totalCount: response.data.length
        };
      } else {
        return {
          apps: [],
          totalCount: 0
        };
      }
    } catch (error: any) {
      console.error('Apps API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createApp = createAsyncThunk(
  'apps/createApp',
  async (appData: CreateAppData, { rejectWithValue }) => {
    try {
      const response = await api.post('/apps/', appData);
      console.log('Create App Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Create App Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateApp = createAsyncThunk(
  'apps/updateApp',
  async (appData: UpdateAppData, { rejectWithValue }) => {
    try {
      const response = await api.put(`/apps/${appData.id}/`, appData);
      console.log('Update App Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Update App Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteApp = createAsyncThunk(
  'apps/deleteApp',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/apps/${id}/`);
      return id;
    } catch (error: any) {
      console.error('Delete App Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const appsSlice = createSlice({
  name: 'apps',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    resetState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch apps
    builder
      .addCase(fetchApps.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApps.fulfilled, (state, action) => {
        state.loading = false;
        state.apps = action.payload.apps;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchApps.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create app
    builder
      .addCase(createApp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createApp.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'App created successfully';
        state.apps.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(createApp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update app
    builder
      .addCase(updateApp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateApp.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'App updated successfully';
        const index = state.apps.findIndex(app => app.id === action.payload.id);
        if (index !== -1) {
          state.apps[index] = action.payload;
        }
      })
      .addCase(updateApp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete app
    builder
      .addCase(deleteApp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteApp.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'App deleted successfully';
        state.apps = state.apps.filter(app => app.id !== action.payload);
        state.totalCount -= 1;
      })
      .addCase(deleteApp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuccess, resetState } = appsSlice.actions;
export default appsSlice.reducer;
